import type { SolvedTrapezoid } from './solver.model';

/**
 * Solve a trapezoid given two parallel sides, base angle, and height.
 * Returns all properties and vertices in canonical placement.
 *
 * Canonical placement: bottom (longer) side along +x.
 * A = (0,0), B = (b,0) -- bottom side
 * D = (h/tan(th), h), C = (h/tan(th) + a, h) -- top side
 * where th is the base angle at A.
 *
 * a = top parallel side, b = bottom parallel side
 */
export function solveTrapezoid(values: Partial<Record<'a' | 'b' | 'h' | 'angle', number>>): SolvedTrapezoid {
  const a = values.a!;
  const b = values.b!;
  const h = values.h!;
  const angleDeg = values.angle!;

  if (a === undefined || b === undefined || h === undefined || angleDeg === undefined) {
    throw new Error('Trapezoid AABH format requires a, b, h, and angle');
  }
  if (a <= 0 || b <= 0 || h <= 0) {
    throw new Error(`Sides and height must be positive: a=${a}, b=${b}, h=${h}`);
  }
  if (angleDeg <= 0 || angleDeg >= 180) {
    throw new Error(`Angle must be between 0° and 180°: angle=${angleDeg}`);
  }

  const angleRad = (angleDeg * Math.PI) / 180;
  const offset = h / Math.tan(angleRad);

  const A: [number, number] = [0, 0];
  const B: [number, number] = [b, 0];
  const D: [number, number] = [offset, h];
  const C: [number, number] = [offset + a, h];

  // Non-parallel side lengths
  const s1 = Math.sqrt(D[0] ** 2 + D[1] ** 2); // A to D
  const s2 = Math.sqrt((B[0] - C[0]) ** 2 + (B[1] - C[1]) ** 2); // B to C

  // Diagonals
  const d1 = Math.sqrt((C[0] - A[0]) ** 2 + (C[1] - A[1]) ** 2); // A to C
  const d2 = Math.sqrt((D[0] - B[0]) ** 2 + (D[1] - B[1]) ** 2); // B to D

  return {
    vertices: { A, B, C, D },
    a,
    b,
    h,
    angle: angleDeg,
    s1,
    s2,
    d1,
    d2,
    area: ((a + b) / 2) * h,
    perimeter: a + b + s1 + s2,
  };
}
